import * as React from 'react';
import { getComputedBackdropBlur } from './getComputedBackdropBlur';
import { renderToStaticMarkup } from 'react-dom/server';
import CombinedBlurFilter from './combinedBlur';
import {
  PAGE_WIDTH,
  SAFE_AREA_HEIGHT,
  FULLBLEED_HEIGHT,
  AMP_DANGER_ZONE_HEIGHT,
} from '@web-stories-wp/units';
import {
  captureLinearLightSource,
  getLinearLightCaptureBounds,
} from './source';
import { flattenLinearLightForCapture } from './capture';

function replaceAmpImages(node: HTMLElement) {
  for (const ampImage of Array.from(node.querySelectorAll('amp-img'))) {
    const image = document.createElement('img');
    image.src = ampImage.getAttribute('src') || '';
    image.className = 'i-amphtml-fill-content';
    image.alt = '';
    image.style.cssText = ampImage.getAttribute('style') || '';
    image.style.objectFit = 'cover';
    ampImage.replaceWith(image);
  }
}

function appendFonts(parsed: Document, buffer: HTMLElement) {
  return Promise.all(
    Array.from(parsed.querySelectorAll('[data-font-def]')).map(
      (font) =>
        new Promise<void>((resolve, reject) => {
          const declaration = document.importNode(font, true);
          if (declaration instanceof HTMLLinkElement) {
            const timeout = setTimeout(
              () => reject(new Error('Timed out loading Linear Light fonts.')),
              10000
            );
            declaration.onload = () => {
              clearTimeout(timeout);
              resolve();
            };
            declaration.onerror = () => {
              clearTimeout(timeout);
              reject(new Error('Unable to load Linear Light fonts.'));
            };
          } else resolve();
          buffer.append(declaration);
        })
    )
  );
}

// AMP forbids feImage and parts of the live SVG filter. Serialize derived pixels
// as AMP images, preserving the hidden editable source for legacy generation.
// Story JSON continues to contain only the original element and blendMode.
export async function prepareLinearLightMarkup(
  markup: string
): Promise<string> {
  if (!markup.includes('data-linear-light-layer')) return markup;
  const parsed = new DOMParser().parseFromString(markup, 'text/html');
  // The body fallback supports isolated OutputElement fixtures as well as stories.
  const pages = Array.from(
    parsed.querySelectorAll<HTMLElement>('amp-story-page')
  );
  if (!pages.length) pages.push(parsed.body);
  for (const page of pages) {
    const originalLayers = Array.from(
      page.querySelectorAll<HTMLElement>('[data-linear-light-layer]')
    );
    if (!originalLayers.length) continue;
    const buffer = document.createElement('div');
    Object.assign(buffer.style, {
      position: 'fixed',
      left: '-100000px',
      top: '0',
      width: `${PAGE_WIDTH}px`,
      height: `${FULLBLEED_HEIGHT}px`,
      opacity: '0',
      pointerEvents: 'none',
    });
    const css = document.createElement('style');
    css.textContent =
      Array.from(parsed.querySelectorAll('style[amp-custom]'))
        .map((style) => style.textContent)
        .join('\n') +
      `
      .linear-light-output-page{position:relative;display:block;width:${PAGE_WIDTH}px;height:${FULLBLEED_HEIGHT}px;font-size:${
        SAFE_AREA_HEIGHT / 10
      }px}
      .linear-light-output-page amp-story-grid-layer{position:absolute;left:0;top:${AMP_DANGER_ZONE_HEIGHT}px;width:100%;height:${SAFE_AREA_HEIGHT}px;display:block;margin:0!important}
      .linear-light-output-page .fill,.linear-light-output-page .mask,.linear-light-output-page .i-amphtml-fill-content{position:absolute;inset:0;width:100%;height:100%}
      .linear-light-output-page .mask{overflow:hidden}`;
    buffer.append(css);
    const root = document.createElement('div');
    root.className = 'linear-light-output-page';
    root.innerHTML = page.innerHTML;
    // Isolated element fixtures use the same safe-area basis as their output.
    if (page === parsed.body) root.style.height = `${SAFE_AREA_HEIGHT}px`;
    replaceAmpImages(root);
    const fonts = appendFonts(parsed, buffer);
    buffer.append(root);
    document.body.append(buffer);
    try {
      await fonts;
      const layers = Array.from(
        root.querySelectorAll<HTMLElement>('[data-linear-light-layer]')
      );
      for (const layer of layers) {
        const source = layer.querySelector<HTMLElement>(
          '[data-linear-light-source]'
        );
        const image = layer.querySelector(
          ':scope > svg feImage[result="source"]'
        );
        if (!source || !image)
          throw new Error('Linear Light markup has no source.');
        const captureBounds = layer.hasAttribute('data-combined-blur')
          ? getLinearLightCaptureBounds(source)
          : undefined;
        image.setAttribute(
          'href',
          await captureLinearLightSource(source, captureBounds)
        );
        // Static rendering starts with the live loading fallback visible.
        // It must never enter its own backdrop during export compositing.
        source.style.opacity = '0';
        if (captureBounds) {
          for (const input of Array.from(
            layer.querySelectorAll(':scope > svg feImage')
          )) {
            for (const name of ['x', 'y', 'width', 'height'] as const)
              input.setAttribute(name, String(captureBounds[name]));
          }
          const filter = image.closest('filter');
          filter?.setAttribute('filterUnits', 'userSpaceOnUse');
          for (const name of ['x', 'y', 'width', 'height'] as const)
            filter?.setAttribute(name, String(captureBounds[name]));
        }
        if (layer.hasAttribute('data-combined-blur')) {
          const mask = layer.querySelector('feImage[result="footprint"]');
          const marker = source.querySelector<HTMLElement>('.element-backdrop');
          if (!mask || !marker)
            throw new Error('Blur markup has no effect footprint.');
          const blur = getComputedBackdropBlur(marker);
          layer.dataset.combinedBlur = String(blur);
          layer.dataset.combinedFeather = String(
            (parseFloat(
              getComputedStyle(marker).getPropertyValue(
                '--backdrop-feather-ratio'
              )
            ) || 0) * blur
          );
          mask.setAttribute(
            'href',
            await captureLinearLightSource(source, captureBounds, {
              skipFonts: true,
              backdropMask: true,
            })
          );
          if (source.querySelector('[data-combined-paint-frame]')) {
            const paintMask = await captureLinearLightSource(
              source,
              captureBounds,
              { skipFonts: true, paintMask: true }
            );
            const filter = image.closest('filter');
            if (!filter) throw new Error('Blur markup has no filter.');
            filter.innerHTML =
              image.outerHTML +
              renderToStaticMarkup(
                React.createElement(CombinedBlurFilter, {
                  mask: mask.getAttribute('href') || undefined,
                  paintMask,
                  blur,
                  feather: Number(layer.dataset.combinedFeather),
                  blendMode: layer.dataset.combinedBlend || 'normal',
                  opacity: Number(layer.dataset.linearLightOpacity),
                  bounds: captureBounds,
                })
              );
          }
        }
        layer.dataset.linearLightStatus = 'ready';
      }
      await flattenLinearLightForCapture(root, { pixelRatio: 1 });
      layers.forEach((layer, index) => {
        const pixels = layer.querySelector<HTMLImageElement>(
          '[data-linear-light-effect] > img'
        );
        const target = originalLayers[index];
        const effect = target.querySelector<HTMLElement>(
          '[data-linear-light-effect]'
        );
        if (!pixels || !effect)
          throw new Error('Linear Light export is unavailable.');
        const bounds = layer.getBoundingClientRect();
        const frame = document.createElement('div');
        Object.assign(frame.style, {
          position: 'absolute',
          left: `${(parseFloat(pixels.style.left) / bounds.width) * 100}%`,
          top: `${(parseFloat(pixels.style.top) / bounds.height) * 100}%`,
          width: `${(parseFloat(pixels.style.width) / bounds.width) * 100}%`,
          height: `${(parseFloat(pixels.style.height) / bounds.height) * 100}%`,
        });
        const image = document.createElement('amp-img');
        image.setAttribute('src', pixels.src);
        image.setAttribute('layout', 'fill');
        image.setAttribute('alt', '');
        frame.append(image);
        effect.replaceChildren(frame);
        effect.style.removeProperty('backdrop-filter');
        effect.style.removeProperty('-webkit-backdrop-filter');
        target.querySelector(':scope > svg')?.remove();
        target.dataset.linearLightStatus = 'ready';
        const native = target.querySelector<HTMLElement>(
          '[data-linear-light-source]'
        );
        if (native) native.style.opacity = '0';
      });
    } finally {
      buffer.remove();
    }
  }
  return parsed.documentElement.outerHTML;
}
